import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { 
  Calculator, 
  DollarSign, 
  TrendingUp, 
  Building2, 
  Download,
  RefreshCw
} from 'lucide-react'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Label } from '../components/ui/Label'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { useHubData } from '../hooks/useHubData'
import { useAuth } from '../contexts/AuthContext'
import { useExchange } from '../contexts/ExchangeContext'

const calculatorSchema = z.object({
  originalCost: z.number().min(0, 'Original cost must be positive'),
  currentValue: z.number().min(0, 'Current value must be positive'),
  sellingPrice: z.number().min(0, 'Selling price must be positive'),
  sellingExpenses: z.number().min(0, 'Selling expenses must be positive'),
  depreciation: z.number().min(0, 'Depreciation must be positive'),
  replacementPropertyCost: z.number().min(0, 'Replacement property cost must be positive'),
})

type CalculatorFormData = z.infer<typeof calculatorSchema>

interface CalculationResults {
  adjustedBasis: number
  realizedGain: number
  recognizedGain: number
  deferredGain: number
  bootReceived: number
  replacementPropertyBasis: number
  taxSavings: number
}

export const CalculatorPage: React.FC = () => {
  const { user } = useAuth()
  const { currentExchange } = useExchange()
  const salesData = useHubData('initial')
  const purchaseData = useHubData('acquisition')
  const [results, setResults] = useState<CalculationResults | null>(null)
  const [isCalculating, setIsCalculating] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm<CalculatorFormData>({
    resolver: zodResolver(calculatorSchema),
    defaultValues: {
      originalCost: 500000,
      currentValue: 750000,
      sellingPrice: 800000,
      sellingExpenses: 40000,
      depreciation: 100000,
      replacementPropertyCost: 900000,
    }
  })

  const watchedValues = watch()

  // Populate calculator from hub data
  useEffect(() => {
    if (!salesData.data && !purchaseData.data) return

    // Extract values from sales agreement and documents
    // For now, we'll use placeholder extraction - in production this would parse actual documents
    const salesAgreement = salesData.data?.documents?.find(d => d.name === 'Purchase/Sale Agreement (Relinquished)')
    const replacementAgreement = purchaseData.data?.documents?.find(d => d.name === 'Purchase Agreement (Replacement)')
    
    // Try to extract from disbursement form amount if available
    const disbursementAmount = purchaseData.data?.disbursementForm?.amount
    if (disbursementAmount) {
      const amount = parseFloat(disbursementAmount.replace(/[^0-9.-]/g, ''))
      if (!isNaN(amount) && amount > 0) {
        setValue('replacementPropertyCost', amount)
      }
    }

    // In production, AI would extract these from uploaded documents:
    // - Original cost from purchase agreement
    // - Selling price from sales agreement
    // - Selling expenses from settlement statement
    // - Depreciation from tax records
    // - Replacement cost from purchase agreement
  }, [salesData.data, purchaseData.data, setValue])

  const calculateResults = (data: CalculatorFormData): CalculationResults => {
    const adjustedBasis = data.originalCost - data.depreciation
    const realizedGain = data.sellingPrice - data.sellingExpenses - adjustedBasis
    const bootReceived = Math.max(0, data.replacementPropertyCost - (data.sellingPrice - data.sellingExpenses))
    const recognizedGain = Math.min(realizedGain, bootReceived)
    const deferredGain = realizedGain - recognizedGain
    const replacementPropertyBasis = data.replacementPropertyCost - deferredGain
    const taxSavings = deferredGain * 0.20 // Assuming 20% capital gains tax rate

    return {
      adjustedBasis,
      realizedGain,
      recognizedGain,
      deferredGain,
      bootReceived,
      replacementPropertyBasis,
      taxSavings
    }
  }

  const onSubmit = async (data: CalculatorFormData) => {
    setIsCalculating(true)
    
    // Simulate calculation delay
    setTimeout(() => {
      const calculatedResults = calculateResults(data)
      setResults(calculatedResults)
      setIsCalculating(false)
    }, 1000)
  }

  const resetForm = () => {
    setValue('originalCost', 0)
    setValue('currentValue', 0)
    setValue('sellingPrice', 0)
    setValue('sellingExpenses', 0)
    setValue('depreciation', 0)
    setValue('replacementPropertyCost', 0)
    setResults(null)
  }

  // Chart data for visualization
  const chartData = results ? [
    { name: 'Original Cost', value: watchedValues.originalCost },
    { name: 'Current Value', value: watchedValues.currentValue },
    { name: 'Selling Price', value: watchedValues.sellingPrice },
    { name: 'Replacement Cost', value: watchedValues.replacementPropertyCost },
  ] : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 flex items-center gap-3">
            <Calculator className="h-7 w-7 text-primary-600" />
            Capital Gains Calculator
          </h1>
          <p className="text-secondary-600 mt-1">
            Calculate your 1031 exchange tax implications and savings
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={resetForm}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button>
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4">Property Information</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="originalCost">Original Purchase Price</Label>
              <Input
                id="originalCost"
                type="number"
                {...register('originalCost', { valueAsNumber: true })}
                placeholder="Enter original purchase price"
              />
              {errors.originalCost && (
                <p className="text-sm text-red-600 mt-1">{errors.originalCost.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="currentValue">Current Market Value</Label>
              <Input
                id="currentValue"
                type="number"
                {...register('currentValue', { valueAsNumber: true })}
                placeholder="Enter current market value"
              />
              {errors.currentValue && (
                <p className="text-sm text-red-600 mt-1">{errors.currentValue.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="sellingPrice">Selling Price</Label>
              <Input
                id="sellingPrice"
                type="number"
                {...register('sellingPrice', { valueAsNumber: true })}
                placeholder="Enter selling price"
              />
              {errors.sellingPrice && (
                <p className="text-sm text-red-600 mt-1">{errors.sellingPrice.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="sellingExpenses">Selling Expenses</Label>
              <Input
                id="sellingExpenses"
                type="number"
                {...register('sellingExpenses', { valueAsNumber: true })}
                placeholder="Enter selling expenses"
              />
              {errors.sellingExpenses && (
                <p className="text-sm text-red-600 mt-1">{errors.sellingExpenses.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="depreciation">Total Depreciation Taken</Label>
              <Input
                id="depreciation"
                type="number"
                {...register('depreciation', { valueAsNumber: true })}
                placeholder="Enter total depreciation"
              />
              {errors.depreciation && (
                <p className="text-sm text-red-600 mt-1">{errors.depreciation.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="replacementPropertyCost">Replacement Property Cost</Label>
              <Input
                id="replacementPropertyCost"
                type="number"
                {...register('replacementPropertyCost', { valueAsNumber: true })}
                placeholder="Enter replacement property cost"
              />
              {errors.replacementPropertyCost && (
                <p className="text-sm text-red-600 mt-1">{errors.replacementPropertyCost.message}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isCalculating}
            >
              {isCalculating ? (
                <>
                  <LoadingSpinner size="sm" variant="icon" className="mr-2" />
                  Calculating...
                </>
              ) : (
                <>
                  <Calculator className="h-4 w-4 mr-2" />
                  Calculate
                </>
              )}
            </Button>
          </form>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          {results && (
            <>
              {/* Key Metrics */}
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-secondary-900 mb-4">Calculation Results</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-5 w-5 text-green-600" />
                      <span className="font-medium text-secondary-900">Tax Savings</span>
                    </div>
                    <span className="text-xl font-bold text-green-600">
                      ${results.taxSavings.toLocaleString()}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <TrendingUp className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-secondary-900">Deferred Gain</span>
                    </div>
                    <span className="text-xl font-bold text-blue-600">
                      ${results.deferredGain.toLocaleString()}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Building2 className="h-5 w-5 text-purple-600" />
                      <span className="font-medium text-secondary-900">New Basis</span>
                    </div>
                    <span className="text-xl font-bold text-purple-600">
                      ${results.replacementPropertyBasis.toLocaleString()}
                    </span>
                  </div>
                </div>
              </Card>

              {/* Detailed Breakdown */}
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-secondary-900 mb-4">Detailed Breakdown</h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Adjusted Basis</span>
                    <span className="font-medium">${results.adjustedBasis.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Realized Gain</span>
                    <span className="font-medium">${results.realizedGain.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Recognized Gain</span>
                    <span className="font-medium">${results.recognizedGain.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Boot Received</span>
                    <span className="font-medium">${results.bootReceived.toLocaleString()}</span>
                  </div>
                </div>
              </Card>

              {/* Chart */}
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-secondary-900 mb-4">Value Progression</h2>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Value']} />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </>
          )}

          {!results && (
            <Card className="p-6">
              <div className="text-center py-8">
                <Calculator className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-secondary-900 mb-2">
                  Ready to Calculate
                </h3>
                <p className="text-secondary-600">
                  Enter your property information to see your 1031 exchange calculations.
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Tips and Information */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-secondary-900 mb-4">Important Notes</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h3 className="font-medium text-secondary-900">1031 Exchange Rules</h3>
            <ul className="text-sm text-secondary-600 space-y-1">
              <li>• Must identify replacement property within 45 days</li>
              <li>• Must close on replacement property within 180 days</li>
              <li>• Must be like-kind property (real estate for real estate)</li>
              <li>• Cannot receive cash or other property (boot)</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h3 className="font-medium text-secondary-900">Tax Implications</h3>
            <ul className="text-sm text-secondary-600 space-y-1">
              <li>• Capital gains tax is deferred, not eliminated</li>
              <li>• Depreciation recapture may apply</li>
              <li>• State taxes may still apply</li>
              <li>• Consult with a tax professional</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  )
}
